import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import "../sections/SectionStyles.css";
import ShimmerLoader from "./ShimmerLoader";
import UploadProgress from "./UploadProgress";
import {
  getFileStructure,
  createFolder,
  uploadFile,
  deleteItem,
  renameItem,
  moveItem,
  getFolderContents,
  searchItems,
} from "../../services/myLibraryService";
import {
  SUPPORTED_VIDEO_FORMATS,
  SUPPORTED_AUDIO_FORMATS,
  isValidMediaFile,
} from "./fileUtils";

// Helper to build folder tree (only folders, no files)
function buildFolderTree(items, parentId = null) {
  return items
    .filter((item) => item.type === "folder" && item.parentId === parentId)
    .map((item) => ({
      ...item,
      children: buildFolderTree(items, item._id),
    }));
}

const getExpandedFolders = (path) => {
  const expanded = {};
  path.forEach((folder) => {
    expanded[folder._id] = true;
  });
  return expanded;
};

function getFileTypeIcon(name) {
  const ext = name.split(".").pop().toLowerCase();
  if (["png", "jpg", "jpeg", "gif", "bmp", "svg"].includes(ext)) return "🖼️";
  if (["pdf"].includes(ext)) return "📄";
  if (["doc", "docx"].includes(ext)) return "📝";
  if (["txt", "md"].includes(ext)) return "📃";
  if (["xls", "xlsx"].includes(ext)) return "📊";
  if (["ppt", "pptx"].includes(ext)) return "📈";
  return "📁";
}

const styles = {
  header: {
    display: "flex",
    alignItems: "center",
    borderBottom: "1px solid #e5e7eb",
    padding: "10px 18px 6px 18px",
    background: "rgba(248,250,252,0.95)",
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    position: "sticky",
    top: 0,
    zIndex: 2,
    gap: 8,
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  fileCard: {
    background: "rgba(255,255,255,0.92)",
    borderRadius: 8,
    boxShadow: "0 1px 4px 0 rgba(31,38,135,0.04)",
    padding: 8,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    transition: "box-shadow 0.2s, background 0.2s",
    border: "1px solid #e0e7ef",
    minHeight: 80,
    minWidth: 0,
    maxWidth: 80,
    position: "relative",
  },
  fileContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    flex: 1,
  },
  fileActions: {
    display: "flex",
    flexDirection: "row",
    gap: 4,
    width: "100%",
    justifyContent: "center",
    position: "absolute",
    bottom: 8,
    left: 0,
    padding: "0 8px",
  },
  deleteButton: {
    color: "#ef4444",
    borderColor: "#fecaca",
    background: "#fff5f5",
  },
};

const FileManager = ({
  onFolderSelect,
  onFileSelect,
  selectionMode = "folder",
}) => {
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [currentFolderContents, setCurrentFolderContents] = useState([]);
  const [currentFolderPath, setCurrentFolderPath] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newFolderName, setNewFolderName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchTimeoutRef = useRef(null);
  const [expanded, setExpanded] = useState({});
  const [uploadState, setUploadState] = useState({
    isUploading: false,
    progress: 0,
    fileName: "",
    fileType: "",
  });

  useEffect(() => {
    const fetchStructure = async () => {
      try {
        setLoading(true);
        const data = await getFileStructure();
        const itemsArray = Array.isArray(data)
          ? data
          : data && Array.isArray(data.data)
          ? data.data
          : [];
        setItems(itemsArray);
        setCurrentFolderContents(itemsArray.filter((item) => !item.parentId));
        setCurrentFolderPath([]);
        setError(null);
      } catch (err) {
        setError(err.message);
        toast.error("Failed to load file structure");
      } finally {
        setLoading(false);
      }
    };
    fetchStructure();
  }, []);

  useEffect(() => {
    if (currentFolderPath.length > 0) {
      const newExpanded = getExpandedFolders(currentFolderPath);
      setExpanded(newExpanded);
    }
  }, [currentFolderPath]);

  useEffect(() => {
    const fetchFolderContents = async () => {
      if (!selectedId) {
        setCurrentFolderContents(
          Array.isArray(items) ? items.filter((item) => !item.parentId) : []
        );
        setCurrentFolderPath([]);
        return;
      }
      try {
        setLoading(true);
        const response = await getFolderContents(selectedId);
        setCurrentFolderContents(response.contents);
        setCurrentFolderPath(response.path);
      } catch (err) {
        toast.error("Failed to load folder contents");
        setCurrentFolderContents([]);
        setCurrentFolderPath([]);
      } finally {
        setLoading(false);
      }
    };
    fetchFolderContents();
  }, [selectedId, items]);

  const handleCreateFolder = async () => {
    if (!newFolderName || newFolderName.trim() === "") {
      toast.error("Please enter a folder name");
      return;
    }
    try {
      setLoading(true);
      await createFolder(newFolderName.trim(), selectedId);

      toast.success("Folder created successfully");
      setShowNewFolderModal(false);
      setNewFolderName("");

      // Refresh file list
      try {
        const data = await getFileStructure();
        setItems(data);
        if (selectedId) {
          const folderData = await getFolderContents(selectedId);
          setCurrentFolderContents(folderData.contents || []);
          setCurrentFolderPath(folderData.path || []);
        }
      } catch (refreshError) {
        console.error("Error refreshing after folder creation:", refreshError);
        toast.error("Folder created but failed to refresh view");
      }
    } catch (error) {
      console.error("Create folder error:", error);
      // Handle API error response
      if (error.message) {
        toast.error(error.message);
      } else if (error.success === false) {
        toast.error(error.message || "Failed to create folder");
      } else {
        toast.error("Failed to create folder: Network or server error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async () => {
    try {
      setLoading(true);
      await deleteItem(selectedId);
      setSelectedId(null);
      const data = await getFileStructure();
      setItems(data);
      setCurrentFolderContents(data.filter((item) => !item.parentId));
      setCurrentFolderPath([]);
      toast.success("Item deleted successfully");
      setShowDeleteModal(false);
    } catch (error) {
      toast.error(error.message || "Failed to delete item");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const extension = file.name.split(".").pop().toLowerCase();
    const isVideo = SUPPORTED_VIDEO_FORMATS.includes(extension);
    const isAudio = SUPPORTED_AUDIO_FORMATS.includes(extension);
    const fileType = isVideo ? "video" : isAudio ? "audio" : "document";

    // Check if it's a media file
    if (isVideo || isAudio) {
      // Validate media file
      if (!isValidMediaFile(file)) {
        toast.error(
          `Invalid ${
            isVideo ? "video" : "audio"
          } file. Please ensure the file is a supported format and under 500MB.`
        );
        event.target.value = "";
        return;
      }
    }

    try {
      setUploadState({
        isUploading: true,
        progress: 0,
        fileName: file.name,
        fileType: file.type || fileType,
      });

      const formData = new FormData();
      formData.append("file", file);
      formData.append("parentId", selectedId || "");

      // Get the API base URL from environment or default
      const baseUrl =
        process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api";
      const token = localStorage.getItem("jwtToken");

      // Create XHR request
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${baseUrl}/filesystem/files`);

      // Set headers
      if (token) {
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      }

      // Track upload progress
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadState((prev) => ({
            ...prev,
            progress,
          }));
        }
      };

      // Handle response
      xhr.onload = async () => {
        try {
          const response = JSON.parse(xhr.responseText);

          if (xhr.status === 200 && response.success) {
            // Refresh file list
            const data = await getFileStructure();
            setItems(data);
            if (selectedId) {
              const folderData = await getFolderContents(selectedId);
              setCurrentFolderContents(folderData.contents);
              setCurrentFolderPath(folderData.path);
            } else {
              setCurrentFolderContents(data.filter((item) => !item.parentId));
            }
            toast.success(`${file.name} uploaded successfully!`);
          } else {
            // Handle API error response
            toast.error(response.message || "Upload failed");
            console.error("Upload API error response:", response);
          }
        } catch (error) {
          console.error("Error parsing upload response:", error);
          toast.error("Failed to process server response");
        } finally {
          setUploadState((prev) => ({ ...prev, isUploading: false }));
        }
      };

      xhr.onerror = () => {
        toast.error("Network error during upload");
        console.error("Upload network error");
        setUploadState((prev) => ({ ...prev, isUploading: false }));
      };

      xhr.send(formData);
    } catch (error) {
      console.error("Upload error:", error);
      if (error.message) {
        toast.error(error.message);
      } else if (error.success === false) {
        toast.error(error.message || "Failed to upload file");
      } else {
        toast.error("Failed to upload file: Network or server error");
      }
      setUploadState((prev) => ({ ...prev, isUploading: false }));
    } finally {
      event.target.value = "";
    }
  };

  const handleRename = async (newName) => {
    try {
      const updatedItem = await renameItem(selectedId, newName);
      setItems((prev) =>
        prev.map((item) => (item._id === selectedId ? updatedItem : item))
      );
      toast.success("Item renamed successfully");
    } catch (error) {
      toast.error("Failed to rename item");
    }
  };

  const handleMove = async (newParentId) => {
    try {
      const updatedItem = await moveItem(selectedId, newParentId);
      setItems((prev) =>
        prev.map((item) => (item._id === selectedId ? updatedItem : item))
      );
      toast.success("Item moved successfully");
    } catch (error) {
      toast.error("Failed to move item");
    }
  };

  const handleToggle = (folderId) => {
    setExpanded((prev) => ({
      ...prev,
      [folderId]: !prev[folderId],
    }));
  };

  const handleSearch = async (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(async () => {
      if (query.trim()) {
        try {
          const results = await searchItems(query);
          setSearchResults(results);
          setShowSuggestions(true);
        } catch (error) {
          toast.error("Failed to search items");
        }
      } else {
        setSearchResults([]);
        setShowSuggestions(false);
      }
    }, 300);
  };

  const handleSuggestionClick = (item) => {
    setSearchQuery(item.name);
    setShowSuggestions(false);
    if (item.type === "folder") {
      setSelectedId(item._id);
    }
  };

  const filteredContents = currentFolderContents.filter((item) =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleFolderSelect = async (folderId) => {
    setSelectedId(folderId);
    if (onFolderSelect) onFolderSelect(folderId);
    try {
      setLoading(true);
      const data = await getFolderContents(folderId);
      setCurrentFolderContents(data.contents || []);
      setCurrentFolderPath(data.path || []);
    } catch (error) {
      setError("Failed to load folder contents");
    } finally {
      setLoading(false);
    }
  };

  const handleFileClick = (e, file) => {
    e.preventDefault();
    e.stopPropagation();
    if (onFileSelect) onFileSelect(file);
  };

  // FolderTree component (left panel)
  const FolderTree = ({ tree, selectedId, onSelect, expanded, onToggle }) => {
    if (!tree || tree.length === 0) return null;
    return (
      <ul style={{ padding: 0, margin: 0, listStyle: "none" }}>
        {tree.map((item) => {
          if (item.type !== "folder") return null;
          return (
            <li key={item._id}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "2px 6px",
                  margin: "1px 0",
                  borderRadius: 4,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  background:
                    selectedId === item._id
                      ? "rgba(37,99,235,0.08)"
                      : "transparent",
                  color: selectedId === item._id ? "#2563eb" : "#64748b",
                  fontWeight: selectedId === item._id ? 600 : 400,
                  fontSize: 13,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect(item._id);
                }}
              >
                {item.children && item.children.length > 0 && (
                  <span
                    style={{
                      marginRight: 4,
                      fontSize: 13,
                      color: selectedId === item._id ? "#2563eb" : "#64748b",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 14,
                      height: 14,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggle(item._id);
                    }}
                  >
                    {expanded[item._id] ? "▼" : "▶"}
                  </span>
                )}
                <span style={{ fontSize: 13, marginRight: 4 }}>📁</span>
                <span
                  style={{
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: 13,
                  }}
                >
                  {item.name}
                </span>
              </div>
              {expanded[item._id] &&
                item.children &&
                item.children.length > 0 && (
                  <div style={{ marginLeft: 10 }}>
                    <FolderTree
                      tree={item.children}
                      selectedId={selectedId}
                      onSelect={onSelect}
                      expanded={expanded}
                      onToggle={onToggle}
                    />
                  </div>
                )}
            </li>
          );
        })}
      </ul>
    );
  };

  const folderTree = buildFolderTree(items);

  console.log('test log added in FileManager', items)

  return (
    <div className="file-manager">
      <div style={styles.header}>
        <div style={styles.actions}>
          <button
            className="btn-primary"
            onClick={() => setShowNewFolderModal(true)}
            disabled={uploadState.isUploading}
          >
            New Folder
          </button>
          <input
            type="file"
            id="file-upload"
            style={{ display: "none" }}
            onChange={handleFileUpload}
            accept={[...SUPPORTED_VIDEO_FORMATS, ...SUPPORTED_AUDIO_FORMATS]
              .map((format) => `.${format}`)
              .join(",")}
            disabled={uploadState.isUploading}
          />
          <button
            className={`btn-primary ${
              uploadState.isUploading ? "uploading" : ""
            }`}
            onClick={() => document.getElementById("file-upload").click()}
            disabled={uploadState.isUploading}
          >
            Upload File
          </button>
        </div>
        <div style={{ flex: 1 }}>
          <input
            type="text"
            placeholder="Search files and folders..."
            value={searchQuery}
            onChange={handleSearch}
            className="search-input"
            style={{ width: "100%" }}
            disabled={uploadState.isUploading}
          />
          {showSuggestions && searchResults.length > 0 && (
            <div className="search-suggestions">
              {searchResults.map((item) => (
                <div
                  key={item._id}
                  className="search-suggestion-item"
                  onClick={() => handleSuggestionClick(item)}
                >
                  {getFileTypeIcon(item.name)} {item.name}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div
        className="file-manager-content"
        style={{ position: "relative", minHeight: "300px" }}
      >
        {loading && !uploadState.isUploading ? (
          <ShimmerLoader count={12} />
        ) : error ? (
          <div className="error-state">
            <p>Error: {error}</p>
            <button onClick={() => window.location.reload()}>Retry</button>
          </div>
        ) : (
          <div
            className="file-grid"
            style={{
              opacity: uploadState.isUploading ? 0.7 : 1,
              transition: "opacity 0.3s ease",
            }}
          >
            {/* Left: Folder Tree */}
            <div
              style={{
                width: 90,
                padding: 4,
                background: "#f3f6fa",
                borderRadius: 8,
                margin: 4,
                marginRight: 2,
                boxShadow: "0 2px 8px 0 rgba(31,38,135,0.04)",
                fontSize: 13,
              }}
            >
              <h4 style={{ marginBottom: 6, color: "#2563eb", fontSize: 13 }}>
                Folders
              </h4>
              <FolderTree
                tree={folderTree}
                selectedId={selectedId}
                onSelect={handleFolderSelect}
                expanded={expanded}
                onToggle={handleToggle}
              />
            </div>
            {/* Right: Main Area */}
            <div
              style={{
                flex: 1,
                background: "rgba(255,255,255,0.92)",
                borderRadius: 8,
                margin: "4px 0 4px 0",
                boxShadow: "0 4px 16px 0 rgba(31,38,135,0.08)",
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              {/* Toolbar - Only show File Manager title */}
              <div
                style={{
                  ...styles.header,
                  padding: "6px 10px 4px 10px",
                  fontSize: 13,
                }}
              >
                <h2 style={{ fontSize: 15, margin: 0 }}>File Manager</h2>
              </div>
              {/* Breadcrumbs */}
              <div
                style={{
                  padding: "4px 10px 0 10px",
                  fontSize: 12,
                  color: "#64748b",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  fontWeight: 500,
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    marginRight: 2,
                    color: "#2563eb",
                    cursor: "pointer",
                  }}
                  onClick={() => setSelectedId(null)}
                >
                  ��
                </span>
                {currentFolderPath.map((crumb, idx) => (
                  <span
                    key={crumb._id}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    {idx > 0 && <span style={{ margin: "0 2px" }}>/</span>}
                    <span
                      style={{
                        cursor:
                          idx < currentFolderPath.length - 1
                            ? "pointer"
                            : "default",
                        color:
                          idx < currentFolderPath.length - 1
                            ? "#2563eb"
                            : "#64748b",
                        fontWeight:
                          idx === currentFolderPath.length - 1 ? 700 : 500,
                        fontSize: 12,
                      }}
                      onClick={() =>
                        idx < currentFolderPath.length - 1 &&
                        setSelectedId(crumb._id)
                      }
                    >
                      {crumb.name}
                    </span>
                  </span>
                ))}
              </div>
              {/* Grid of Folders and Files */}
              <div
                style={{
                  flex: 1,
                  padding: 8,
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: 2,
                  alignContent: "flex-start",
                  overflowY: "auto",
                  minWidth: 0,
                }}
              >
                {filteredContents.map((item) => (
                  <div
                    key={item._id}
                    className={`file-item-glassy ${
                      item.type === "folder" ? "folder" : "file"
                    }`}
                    onClick={
                      item.type === "folder"
                        ? () => handleFolderSelect(item._id)
                        : (e) => handleFileClick(e, item)
                    }
                    style={{
                      ...styles.fileCard,
                      cursor: item.type === "folder" ? "pointer" : "default",
                      background:
                        item.type === "folder"
                          ? "rgba(236,244,255,0.85)"
                          : "rgba(255,255,255,0.92)",
                      fontSize: 12,
                      minHeight: 60,
                      maxWidth: 60,
                      padding: 6,
                    }}
                  >
                    <div style={{ ...styles.fileContent, fontSize: 12 }}>
                      <div
                        className="file-icon-glassy"
                        style={{
                          fontSize: item.type === "folder" ? 14 : 12,
                          color: item.type === "folder" ? "#2563eb" : "#64748b",
                          marginBottom: item.type === "folder" ? 2 : 1,
                          filter:
                            item.type === "folder"
                              ? "drop-shadow(0 2px 6px #c7d2fe)"
                              : "none",
                        }}
                      >
                        {item.type === "folder"
                          ? "📁"
                          : getFileTypeIcon(item.name)}
                      </div>
                      <div
                        className="file-name-glassy"
                        style={{
                          fontWeight: item.type === "folder" ? 600 : 500,
                          fontSize: item.type === "folder" ? 11 : 10,
                          color: item.type === "folder" ? "#1e293b" : "#334155",
                          textAlign: "center",
                          wordBreak: "break-all",
                          lineHeight: 1.2,
                          maxWidth: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          marginBottom: item.type === "file" ? 1 : 0,
                        }}
                      >
                        {item.name || ""}
                      </div>
                      {item.type === "file" && (
                        <div
                          className="file-size-glassy"
                          style={{ fontSize: 9, color: "#64748b" }}
                        >
                          {item?.fileMeta?.size
                            ? `${(item?.fileMeta?.size / 1024).toFixed(1)} KB`
                            : ""}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {uploadState.isUploading && (
          <UploadProgress
            fileName={uploadState.fileName}
            progress={uploadState.progress}
            fileType={uploadState.fileType}
          />
        )}
      </div>

      <style jsx>{`
        .file-manager {
          display: flex;
          flex-direction: column;
          height: 100%;
          background: #f8fafc;
          border-radius: 12px;
          overflow: hidden;
        }

        .file-manager-content {
          flex: 1;
          overflow: auto;
          padding: 20px;
        }

        .btn-primary.uploading {
          cursor: not-allowed;
          opacity: 0.7;
          transition: opacity 0.3s ease;
        }

        .error-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px;
          text-align: center;
          color: #ef4444;
        }

        .error-state button {
          margin-top: 16px;
          padding: 8px 16px;
          background: #ef4444;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          transition: background 0.2s;
        }

        .error-state button:hover {
          background: #dc2626;
        }
      `}</style>
    </div>
  );
};

export default FileManager;
